import { useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconPlusOutline18 as Plus,
	IconStarOutline18 as Star,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"

import { pro } from "@productlane/lib"
import {
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	LanguageTag,
	Popover,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"

import type { WorkspaceLanguages } from "./workspace-languages"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { useRequiredPlans } from "@/lib/plans"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "@/screens/settings/plan-pill"
import { SettingsHeader } from "@/screens/settings/settings-header"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { getLanguageByCode, LANGUAGES } from "./language-selector"
import {
	addLanguage,
	canRemoveLanguage,
	makeDefaultLanguage,
	orderDefaultFirst,
	removeLanguage,
	resolveWorkspaceLanguages,
} from "./workspace-languages"

export function LanguagesSettingsPage() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const navigate = useNavigate()
	const [isAddLanguageOpen, setIsAddLanguageOpen] = useState(false)

	const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

	const languages = useMemo(
		() =>
			resolveWorkspaceLanguages({
				defaultLanguage: workspace?.defaultLanguage,
				enabledLanguages: workspace?.enabledLanguages,
			}),
		[workspace?.defaultLanguage, workspace?.enabledLanguages],
	)

	const rows = useMemo(
		() =>
			orderDefaultFirst(languages)
				.map(getLanguageByCode)
				.filter((l) => !!l),
		[languages],
	)

	const availableLanguages = useMemo(
		() =>
			LANGUAGES.filter(
				(lang) => !languages.enabledLanguages.includes(lang.code),
			),
		[languages.enabledLanguages],
	)

	const save = async (next: WorkspaceLanguages) => {
		if (!workspace) return
		if (!hasRequiredPlan) {
			toast.info({
				title: "Upgrade to Pro to change languages",
				action: {
					label: "View plans",
					callback: () => {
						void navigate({ to: "/settings/plans" })
					},
				},
			})
			return
		}
		await mutateOk(
			zero.mutate(
				mutators.workspaces.update({
					id: workspace.id,
					defaultLanguage: next.defaultLanguage,
					enabledLanguages: next.enabledLanguages,
				}),
			),
		)
	}

	const handleAdd = async (code: string) => {
		setIsAddLanguageOpen(false)
		await save(addLanguage(languages, code))
	}

	// Removing keeps the language's translations and versions; rendering filters
	// by enabledLanguages, so Undo or re-adding it brings the content back.
	const handleRemove = async (code: string) => {
		const next = removeLanguage(languages, code)
		if (!next || !hasRequiredPlan) return
		const previous = languages
		await save(next)
		toast.success({
			title: `${getLanguageByCode(code)?.name ?? code} removed`,
			description: "Its translations are kept.",
			onUndo: () => save(previous),
		})
	}

	const languageCount = languages.enabledLanguages.length

	return (
		<>
			<SettingsHeader
				title="Language"
				subtitle="Visitors see your portal and widget in their browser language. Everyone else sees the default."
				docsArticle="09a09ced-ff09-487a-bf03-080ad0e64e2d"
				titleSuffix={<PlanPill plan="pro" />}
			/>

			<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
				<div className="flex flex-col">
					<div className="flex items-center justify-between border-b border-gray-200 px-4 pb-4 dark:border-gray-700/80">
						<div className="text-sm font-medium">
							{languageCount} {languageCount === 1 ? "language" : "languages"}
						</div>
						{hasRequiredPlan ? (
							<Popover
								open={isAddLanguageOpen}
								onOpenChange={setIsAddLanguageOpen}
								content={
									<Command>
										<CommandInput
											placeholder="Search languages..."
											showIcon={true}
										/>
										<CommandList>
											<CommandEmpty>No language found.</CommandEmpty>
											<CommandGroup>
												{availableLanguages.map((language) => (
													<CommandItem
														key={language.code}
														value={`${language.code.toUpperCase()} ${language.name}`}
														onSelect={() => {
															void handleAdd(language.code)
														}}
													>
														<div className="flex items-center gap-2">
															<LanguageTag
																className="min-w-8 text-center"
																code={language.code}
															/>
															<span>{language.name}</span>
														</div>
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								}
								className="min-w-[240px] p-0"
								align="end"
							>
								<Button variant="ghost" Icon={Plus} className="text-primary">
									Add language
								</Button>
							</Popover>
						) : (
							<UpgradeButton required={pro.andUp} />
						)}
					</div>

					{rows.map((language) => {
						const isDefault = language.code === languages.defaultLanguage
						return (
							<div
								key={language.code}
								className="flex min-h-14 items-center gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0 dark:border-gray-700/80"
							>
								<LanguageTag
									className="min-w-8 text-center"
									code={language.code}
								/>
								<span className="truncate text-sm font-medium">
									{language.name}
								</span>
								{isDefault && <Badge>Default</Badge>}
								{hasRequiredPlan && (
									<div className="ml-auto">
										<DropdownMenu
											trigger={
												<IconButton Icon={MoreHorizontal} variant="ghost" />
											}
											contentProps={{ align: "end" }}
										>
											<DropdownMenuItem
												Icon={Star}
												disabled={isDefault}
												onSelect={() => {
													void save(
														makeDefaultLanguage(languages, language.code),
													)
												}}
											>
												Make default
											</DropdownMenuItem>
											<DropdownMenuItem
												Icon={Trash2}
												disabled={!canRemoveLanguage(languages)}
												onSelect={() => {
													void handleRemove(language.code)
												}}
											>
												Remove
											</DropdownMenuItem>
										</DropdownMenu>
									</div>
								)}
							</div>
						)
					})}
				</div>
			</SettingsContainer>
		</>
	)
}
